import { useEffect, useRef, type ReactNode } from 'react'
import { NOM } from '@/components/brand/nom'
import type { Faculty } from '@/lib/api/auth'
import { BottomTabs } from './BottomTabs'
import { EtatDuReseau } from './EtatDuReseau'
import { NavRail } from './NavRail'
import { RightRail } from './RightRail'
import { NAV, NAV_ATELIER, NAV_EDITORIAL } from './navigation'

interface Props {
  active: string
  displayName: string
  dailyGoalXp: number
  /** 02 §F7.1 — ouvre l'entrée du back-office. */
  editorial?: boolean
  faculties: Faculty[]
  onNavigate: (id: string) => void
  header?: ReactNode
  /**
   * Ce que porte la barre supérieure, à droite — RG-D22.
   *
   * Le visage et son message, quand le Fil est à l'écran. `undefined`
   * ailleurs : commenter la performance devant un écran d'archives n'aurait
   * aucun sens.
   */
  entete?: ReactNode
  /**
   * Le coin GAUCHE de l'en-tête — §88.
   *
   * Le visage occupe la droite ; ce qui relève d'une commande — rafraîchir le
   * fil — se pose à gauche, là où l'œil part.
   */
  enteteGauche?: ReactNode
  /**
   * L'espace où l'on se trouve — [DECISIONS §115].
   *
   * Il traverse la coque entière : le rail, la barre du bas et la colonne
   * latérale servent tous les trois le candidat, et aucun des trois n'a de sens
   * dans l'atelier éditorial.
   */
  espace?: 'candidat' | 'editorial'
  children: ReactNode
}

/**
 * Coque applicative — 06 §5, disposition Twitter à toutes les tailles.
 *
 *   < 688 px    barre supérieure + colonne unique + barre d'onglets en bas ;
 *   ≥ 688 px    rail d'icônes à gauche, colonne centrale de 600 px ;
 *   ≥ 1005 px   colonne latérale droite de 350 px ;
 *   ≥ 1265 px   rail étendu avec libellés, ensemble centré sur 1265 px.
 *
 * La colonne centrale est plafonnée à 600 px À TOUTES les tailles : un fil
 * étalé sur 1 400 px est illisible (RG-D8). C'est la même contrainte que
 * Twitter s'impose, et pour la même raison.
 */
export function AppShell({
  active,
  displayName,
  dailyGoalXp,
  editorial = false,
  faculties,
  onNavigate,
  header,
  entete,
  enteteGauche,
  espace = 'candidat',
  children,
}: Props) {
  // Le titre de la barre supérieure se lit dans la même liste que le rail,
  // sans quoi l'écran éditorial afficherait un en-tête vide. `NAV_ATELIER` y
  // figure depuis §115 : sans lui, les cinq postes éditoriaux rendaient tous le
  // même titre de repli, et un lecteur d'écran ne savait plus où il était.
  const courant = [...NAV, NAV_EDITORIAL, ...NAV_ATELIER].find((entry) => entry.id === active)

  // **L'en-tête MESURE sa hauteur et la publie** — §147. Les onglets se collent
  // dessous sans qu'aucun nombre soit recopié chez eux : la bande porte la
  // hauteur de ses enfants — le visage, la commande de rafraîchissement — et se
  // referme d'elle-même quand ils ne rendent rien. Une valeur écrite en dur
  // serait fausse la moitié du temps, et fausse en silence.
  const bandeHaute = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bande = bandeHaute.current

    if (bande === null) {
      return
    }

    const observateur = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--haut-entete',
        String(Math.round(bande.getBoundingClientRect().height)) + 'px',
      )
    })

    observateur.observe(bande)

    return () => { observateur.disconnect(); }
  }, [])

  return (
    <div className="mx-auto flex w-full max-w-[1265px] justify-center">
      <NavRail
        editorial={editorial}
        espace={espace}
        active={active}
        displayName={displayName}
        onNavigate={onNavigate}
      />

      <div className="flex w-full min-w-0 max-w-[600px] flex-col border-x" style={{ borderColor: 'var(--bordure)' }}>
        {/* Barre supérieure translucide et collante — la signature Twitter.
            **Elle porte le visage** (RG-D22 amendée), et plus l'avatar ni le
            titre : l'avatar n'ouvrait que le compte, qui a son entrée dans le
            rail, et le titre répétait l'onglet actif déjà souligné en dessous.
            Le visage, lui, commente ce qui vient de se passer et doit rester
            visible pendant le défilement — ce qu'une pastille flottante ne
            faisait pas sans recouvrir le bouton « Réviser ».
            `justify-end` : le groupe visage + message est calé à DROITE, et
            s'étend vers la gauche à mesure que le texte s'écrit. */}
        <div
          // **Sans contenu, la barre ne prend AUCUNE hauteur.** Elle réservait
          // 53 px en permanence : pendant une lecture, où le visage se tait
          // (RG-D22ter), cela faisait une bande blanche en haut de l'écran qui
          // mangeait autant de texte — sur un téléphone, une ligne et demie
          // perdue à chaque page.
          // **La hauteur vient du CONTENU, pas de la présence d'un prop.**
          // `entete` est un élément — `<VisageDeLEntete />` — donc toujours
          // « défini », même quand le visage se tait (RG-D22ter). La bande
          // gardait ses 53 px pendant une épreuve, et l'en-tête collant du quiz
          // se glissait dessous : le rang et le chronomètre disparaissaient au
          // premier défilement (§108).
          //
          // Ce sont donc les ENFANTS qui portent leur hauteur — le visage la
          // sienne, la commande de rafraîchissement la sienne — et la bande se
          // referme d'elle-même quand ils ne rendent rien.
          ref={bandeHaute}
          className="sticky top-0 z-10 flex items-center justify-between gap-4 px-4 backdrop-blur-md"
          style={{ background: 'color-mix(in srgb, var(--fond) 85%, transparent)' }}
        >
          {/* Le titre reste pour les lecteurs d'écran : c'est le repère de
              position que la barre du bas ne donne pas au clavier. */}
          <h1 className="sr-only">{courant?.label ?? NOM}</h1>

          {/* `justify-between` avec un premier enfant vide : la droite reste
              calée à droite même sans commande à gauche. */}
          <span className="flex items-center">{enteteGauche}</span>
          <span className="flex items-center gap-4">{entete}</span>
        </div>

        {/* **L'état du réseau, sous la barre supérieure** — RG-9.7, §111. Posé
            ICI plutôt que dans le Fil : une réponse mise en file peut venir
            d'un défi ou d'un quiz de section, et l'état de la connexion ne
            dépend d'aucun écran. Il ne rend rien quand il n'a rien à dire. */}
        <EtatDuReseau />

        {header}

        {/* pb-[53px] : la barre d'onglets est en position fixe et masquerait
            sinon le dernier item du fil. */}
        {/* **`pt-3` : les écrans respirent en haut** — RG-D6bis, §87. §70 a
            retiré la hauteur que la barre supérieure réservait quand elle ne
            porte rien — ce qui était juste, cette bande vide mangeait une ligne
            et demie de texte. Mais plus rien ne séparait alors le contenu du
            bord : le catalogue des cours, le quiz de section, les archives et
            le plan d'un cours commençaient tous au pixel zéro.
            Posé ICI plutôt qu'écran par écran : le défaut se reproduisait à
            chaque écran ajouté, et douze corrections locales en auraient
            manqué une. */}
        <main className="flex flex-1 flex-col pt-3 pb-[53px] min-[688px]:pb-0">{children}</main>
      </div>

      {/* **La colonne latérale reste au candidat** — §115. Objectif du jour,
          série, concours principal : rien de tout cela ne concerne un
          relecteur, et la place vaut mieux à la colonne de travail, qui reçoit
          des listes longues. */}
      {espace === 'candidat' && (
        <RightRail dailyGoalXp={dailyGoalXp} faculties={faculties} onNavigate={onNavigate} />
      )}

      {/* **Le bouton flottant « Réviser » a été retiré** (7 août 2026). Il
          menait au Fil — qui a déjà son entrée dans le rail ET en première
          position de la barre du bas. Un troisième chemin vers le même écran
          occupait le coin le plus précieux de la page, recouvrait l'objectif du
          jour, et masquait les options d'une question à choix multiple. Ce coin
          revient à la pastille d'objectif (RG-2.41).

          Le bouton rond de la barre du bas, lui, n'est PAS un retour de
          celui-ci : il ne mène nulle part, il OUVRE le reste de la navigation
          (RG-D25). */}

      {/* Le concours principal descend jusqu'à la feuille : elle n'annonce plus
          « Concours préparé » mais « Principal · FMP ». Le mot seul disait où
          l'on va sans dire où l'on est, et c'est cette seconde information
          qu'on vient chercher (§78). */}
      <BottomTabs
        active={active}
        editorial={editorial}
        espace={espace}
        concoursPrincipal={faculties.find((f) => f.is_primary === true)?.short_name ?? null}
        onNavigate={onNavigate}
      />
    </div>
  )
}
